import React from "react";
import "./ProductAttributes.scss";
import AttributeTile from "./AttributeTile";
import getTranslationData from "../../TranslationUtility/TranslationUtility";
import usePurchaseLimit from "../Card/hooks/usePurchaseLimit";
import { ShieldLocked, UsageCheck } from "../Icons";

/* Attribute set shown under the product name.
   Only attributes with a value are built, so a missing one leaves no gap or
   placeholder. Two or more render as the attribute grid; a single one renders
   as one inline row.
   include limits which attributes are considered; all three are considered when
   it is omitted. layout "inline" puts an attribute on one line, for the single
   value shown at checkout. */
const ProductAttributes = ({
  redeemableType = "",
  validity,
  productBudget,
  productId,
  include,
  layout = "tile",
  className = "",
}) => {
  const translation = getTranslationData();
  const { hasLimit, remaining } = usePurchaseLimit(productBudget, productId);

  // Usage wording is its own set of keys so the standalone redeem pill keeps
  // its phrasing.
  const usageByStatus = {
    "": {
      text: translation?.usageOnline || "Online",
    },
    "Redeemable Online": {
      text: translation?.usageOnline || "Online",
    },
    "Redeemable Offline": {
      text: translation?.usageOffline || "In-store",
    },
    "Redeemable Online & Offline": {
      text: translation?.usageBoth || "Online & in-store",
    },
  };

  const attributes = [];
  const isIncluded = (key) => !include || include.includes(key);

  const usage = usageByStatus[redeemableType];
  if (isIncluded("usage") && usage?.text) {
    attributes.push({
      key: "usage",
      label: translation?.usage || "Usage",
      value: usage.text,
      icon: <UsageCheck />,
    });
  }

  const months = Number(validity);
  if (isIncluded("validity") && validity && Number.isFinite(months) && months > 0) {
    attributes.push({
      key: "validity",
      label: translation?.validFor || "Valid for",
      value: `${months} ${
        months === 1
          ? translation?.month || "Month"
          : translation?.months || "Months"
      }`,
      icon: <i className="fa-regular fa-calendar-check"></i>,
      labelFirst: true,
    });
  }

  if (isIncluded("limit") && hasLimit) {
    attributes.push({
      key: "limit",
      label: translation?.rem_limit_heading || "Remaining Limit",
      value: `₹${Number(remaining || 0).toLocaleString()}`,
      icon: <ShieldLocked />,
    });
  }

  if (!attributes.length) return null;

  const isInline = layout === "inline";

  return (
    <div
      className={`product-attrs ${
        isInline ? "product-attrs-inline" : ""
      } ${className}`.trim()}
    >
      {attributes.map((attribute) => (
        <AttributeTile
          key={attribute.key}
          icon={attribute.icon}
          label={attribute.label}
          value={attribute.value}
          layout={layout}
          labelFirst={attribute.labelFirst}
        />
      ))}
    </div>
  );
};

export default ProductAttributes;
